import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useDataProvider, useGetOne, useNotify } from "ra-core";
import { useNavigate } from "react-router";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import type { Company, Contact } from "@/components/atomic-crm/types";
import { getClientShowPath } from "@/app/routing";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FloatingTextField } from "@/components/ui/floating-field";
import { Label } from "@/components/ui/label";
import { LBS_LEAD_STATUSES } from "@/app/navigation";

export const ConvertLeadButton = ({ record }: { record: Contact }) => {
  const [open, setOpen] = useState(false);
  const [companyName, setCompanyName] = useState(record.company_name ?? "");
  const [createDeal, setCreateDeal] = useState(true);
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const navigate = useNavigate();

  const isLead =
    record.status != null &&
    (LBS_LEAD_STATUSES as readonly string[]).includes(record.status);

  const hasConvert =
    "convertLeadToClient" in dataProvider &&
    typeof (dataProvider as CrmDataProvider & { convertLeadToClient?: unknown })
      .convertLeadToClient === "function";

  const hasExistingCompany = record.company_id != null;

  const { data: existingCompany } = useGetOne<Company>(
    "companies",
    { id: record.company_id as number },
    { enabled: hasExistingCompany && open },
  );

  const resolvedCompanyName =
    existingCompany?.name ?? record.company_name ?? "";

  useEffect(() => {
    if (hasExistingCompany && resolvedCompanyName) {
      setCompanyName(resolvedCompanyName);
    }
  }, [hasExistingCompany, resolvedCompanyName]);

  useEffect(() => {
    if (open) {
      setCreateDeal(true);
    }
  }, [open]);

  const { mutate: convertLead, isPending } = useMutation({
    mutationFn: () =>
      (
        dataProvider as CrmDataProvider & {
          convertLeadToClient: (params: {
            contactId: Contact["id"];
            companyName: string;
            createDeal?: boolean;
          }) => Promise<{
            company_id: number;
            contact_id: number;
            deal_id: number | null;
          }>;
        }
      ).convertLeadToClient({
        contactId: record.id,
        companyName: hasExistingCompany
          ? resolvedCompanyName || record.company_name || ""
          : companyName,
        createDeal,
      }),
    onSuccess: ({ company_id, deal_id }) => {
      if (deal_id != null) {
        notify("Converted to client and project created");
      } else {
        notify("Converted to client");
      }
      setOpen(false);
      if (company_id != null) navigate(getClientShowPath(company_id));
    },
    onError: (error: Error) => {
      notify(error.message || "Failed to convert lead", { type: "error" });
    },
  });

  const canSubmit = useMemo(() => {
    if (isPending) return false;
    if (hasExistingCompany) return resolvedCompanyName.trim().length > 0;
    return companyName.trim().length >= 2;
  }, [companyName, hasExistingCompany, isPending, resolvedCompanyName]);

  if (!isLead || !hasConvert) {
    return null;
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Convert to client
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert lead to client</DialogTitle>
            {hasExistingCompany ? (
              <DialogDescription>
                This lead is already linked to the bill-to company{" "}
                <span className="font-medium">
                  {resolvedCompanyName || "an existing company"}
                </span>
                . Converting promotes them into the client journey and sets this
                contact as the primary.
              </DialogDescription>
            ) : (
              <DialogDescription>
                Creates or links a bill-to company and moves this person into
                the client journey. Invoices and tickets will use that company.
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            {!hasExistingCompany ? (
              <FloatingTextField
                id="company-name"
                label="Client company name"
                value={companyName}
                onChange={setCompanyName}
                placeholder="Acme Corp"
              />
            ) : null}
            <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-3">
              <Checkbox
                id="create-deal"
                checked={createDeal}
                onCheckedChange={(value) => setCreateDeal(value === true)}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="create-deal"
                  className="cursor-pointer font-medium"
                >
                  Also create a project for this client
                </Label>
                <p className="text-xs text-muted-foreground">
                  Recommended. A project is opened in stage{" "}
                  <span className="font-medium">Closed Won</span> so it shows up
                  in Deals right away. You can edit details afterwards.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => convertLead()} disabled={!canSubmit}>
              Convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
